import { useMutation, useQuery } from '@tanstack/react-query';
import { toolsService } from '@/modules/gpt-chats/services/tools.service';
import type {
  IGetToolsPayload,
  IMCPServerSetupPayload,
  ISaveAuthConfigPayload,
  ICreateApiActionPayload,
  IToolSummaryPayload,
  ITestApiActionPayload,
  IGetOpenToolsPayload,
  ISaveTutorialPayload,
} from '@/modules/gpt-chats/types/tools.service.type';

export const useGetTools = (payload: IGetToolsPayload, enabled = true) => {
  return useQuery({
    queryKey: ['tools', payload],
    queryFn: () => toolsService.getTools(payload),
    enabled,
  });
};

export const useAddMCPServer = () => {
  return useMutation({
    mutationKey: ['tools', 'add-mcp-server'],
    mutationFn: toolsService.addMCPServer,
  });
};

export const useAddAPITool = () => {
  return useMutation({
    mutationKey: ['tools', 'add-api-tool'],
    mutationFn: toolsService.addAPITool,
  });
};

export const useGetToolDetails = (toolId: string, isBlocksTool?: boolean, enabled = true) => {
  return useQuery({
    queryKey: ['tools', 'details', toolId, isBlocksTool],
    queryFn: () => toolsService.getToolDetails(toolId, isBlocksTool),
    enabled: enabled && !!toolId,
  });
};

export const useGetOpenToolDetails = (toolId: string, enabled = true) => {
  return useQuery({
    queryKey: ['tools', 'open-tool-details', toolId],
    queryFn: () => toolsService.getOpenToolDetails(toolId),
    enabled: enabled && !!toolId,
  });
};

export const useUpdateMCPServer = () => {
  return useMutation({
    mutationKey: ['tools', 'update-mcp-server'],
    mutationFn: toolsService.updateMCPServer,
  });
};

export const useUpdateAPITool = () => {
  return useMutation({
    mutationKey: ['tools', 'update-api-tool'],
    mutationFn: toolsService.updateAPITool,
  });
};

export const useDeleteTool = () => {
  return useMutation({
    mutationKey: ['tools', 'delete-tool'],
    mutationFn: ({ toolId, isBlocksTool }: { toolId: string; isBlocksTool?: boolean }) =>
      toolsService.deleteTool(toolId, isBlocksTool),
  });
};

export const useSaveAuthConfig = () => {
  return useMutation({
    mutationKey: ['tools', 'save-auth-config'],
    mutationFn: ({ toolId, payload }: { toolId: string; payload: ISaveAuthConfigPayload }) =>
      toolsService.saveAuthConfig(toolId, payload),
  });
};

export const useCreateApiAction = () => {
  return useMutation({
    mutationKey: ['tools', 'create-api-action'],
    mutationFn: ({
      toolId,
      actionId,
      payload,
    }: {
      toolId: string;
      actionId?: string;
      payload: ICreateApiActionPayload;
    }) => toolsService.createApiAction(toolId, payload, actionId),
  });
};

export const useDeleteToolApiAction = () => {
  return useMutation({
    mutationKey: ['tools', 'delete-tool-api-action'],
    mutationFn: ({
      toolId,
      actionId,
      isBlocksTool,
    }: {
      toolId: string;
      actionId: string;
      isBlocksTool?: boolean;
    }) => toolsService.deleteToolApiAction(toolId, actionId, isBlocksTool),
  });
};

export const useSaveTutorial = () => {
  return useMutation({
    mutationKey: ['tools', 'save-tutorial'],
    mutationFn: ({ toolId, payload }: { toolId: string; payload: ISaveTutorialPayload }) =>
      toolsService.saveTutorial(toolId, payload),
  });
};

export const useSetupMCPServer = () => {
  return useMutation({
    mutationKey: ['tools', 'setup-mcp-server'],
    mutationFn: ({ toolId, payload }: { toolId: string; payload: IMCPServerSetupPayload }) =>
      toolsService.setupMCPServer(toolId, payload),
  });
};

export const useRefreshMCPServer = () => {
  return useMutation({
    mutationKey: ['tools', 'refresh-mcp-server'],
    mutationFn: ({ toolId, isBlocksTool }: { toolId: string; isBlocksTool?: boolean }) =>
      toolsService.refreshMCPServer(toolId, isBlocksTool),
  });
};

export const useTestApiAction = () => {
  return useMutation({
    mutationKey: ['tools', 'test-api-action'],
    mutationFn: ({
      toolId,
      actionId,
      payload,
    }: {
      toolId: string;
      actionId: string;
      payload: ITestApiActionPayload;
    }) => toolsService.testApiAction(toolId, actionId, payload),
  });
};

export const useGetToolSchema = (toolId: string, isBlocksTool?: boolean, enabled = true) => {
  return useQuery({
    queryKey: ['tools', 'schema', toolId, isBlocksTool],
    queryFn: () => toolsService.getToolSchema(toolId, isBlocksTool),
    enabled: enabled && !!toolId,
  });
};

export const useTestMcpAuth = () => {
  return useMutation({
    mutationKey: ['tools', 'test-mcp-auth'],
    mutationFn: ({
      toolId,
      payload,
      isBlocksTool,
    }: {
      toolId: string;
      payload: { auth_config: Record<string, unknown> };
      isBlocksTool?: boolean;
    }) => toolsService.testMcpAuth(toolId, payload, isBlocksTool),
  });
};

export const useInitiateOAuth = () => {
  return useMutation({
    mutationKey: ['tools', 'initiate-oauth'],
    mutationFn: ({
      toolId,
      payload,
      isBlocksTool,
    }: {
      toolId: string;
      payload: { oauth_config: Record<string, unknown> };
      isBlocksTool?: boolean;
    }) => toolsService.initiateOAuth(toolId, payload, isBlocksTool),
  });
};

export const useCompleteOAuth = () => {
  return useMutation({
    mutationKey: ['tools', 'complete-oauth'],
    mutationFn: ({
      toolId,
      payload,
    }: {
      toolId: string;
      payload: { code: string; state: string; is_open_tool?: boolean };
    }) => toolsService.completeOAuth(toolId, payload),
  });
};

export const useToolSummary = (payload: IToolSummaryPayload) => {
  return useQuery({
    queryKey: ['tools', 'summary', payload],
    queryFn: () => toolsService.getToolSummary(payload),
  });
};

export const useToolAssociatedAgentCount = (toolId: string, enabled = true) => {
  return useQuery({
    queryKey: ['tools', toolId, 'agent-count'],
    queryFn: () => toolsService.getToolAssociatedAgentCount(toolId),
    enabled,
  });
};

export const useGetOpenTools = (payload: IGetOpenToolsPayload) => {
  return useQuery({
    queryKey: ['tools', 'open-tools', payload],
    queryFn: () => toolsService.getOpenTools(payload),
  });
};

export const useAddOpenTool = () => {
  return useMutation({
    mutationKey: ['tools', 'add-open-tool'],
    mutationFn: ({ toolId }: { toolId: string }) => toolsService.addOpenTool(toolId),
  });
};
